import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import type {
  SolarProposalVppProgramsDto,
  UpdateSolarProposalVppProgramsDto,
} from "../dto/solar-proposal-pricing-steps.dto"
import { solarProposalService } from "../services/solar-proposal.service"

/**
 * Saves the selected programs and acknowledges the removals the drawer read.
 * No pricing impact; the estimates feed savings, the pricing summary and the
 * contract's freshness blockers.
 */
export default function useUpdateSolarProposalVppPrograms(): UseMutationResult<
  SolarProposalVppProgramsDto,
  unknown,
  { proposalId: string; body: UpdateSolarProposalVppProgramsDto }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proposalId, body }) =>
      solarProposalService.putVppPrograms(proposalId, body),
    onSuccess: (programs, { proposalId }) => {
      queryClient.setQueryData(
        queryKeys.solarProposal.vppPrograms(proposalId),
        programs,
      )
      for (const queryKey of [
        queryKeys.solarProposal.savings(proposalId),
        queryKeys.solarProposal.pricing(proposalId),
        queryKeys.solarProposal.contract(proposalId),
        queryKeys.solarProposal.share(proposalId),
      ]) {
        void queryClient.invalidateQueries({ queryKey, exact: true })
      }
    },
  })
}
